import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useSelector } from "react-redux";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import Sidebar from "../../components/sidebar/Sidebar";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import { useUserRole } from "../../hooks/useUserRole";
import { useGetControlBoardQuery } from "../../store/apis/controlBoardApi";
import { buildBoardData } from "../../components/control-board/boardData";
import SummaryTab from "../../components/control-board/SummaryTab";
import BoardTab from "../../components/control-board/BoardTab";
import ClientTab from "../../components/control-board/ClientTab";
import TechniciansTab from "../../components/control-board/TechniciansTab";
import AccountsTab from "../../components/control-board/AccountsTab";
import CostsTab from "../../components/control-board/CostsTab";
import PricesTab from "../../components/control-board/PricesTab";
import MarginsTab from "../../components/control-board/MarginsTab";
import { MONTHS } from "../../components/control-board/controlBoardModel";

/** Quién lo ve: lleva el coste neto de plataforma y el plan de la agencia. */
export const CONTROL_BOARD_ROLES = ["ADMIN", "PRICING"];

const TABS = [
  { value: "summary", label: "Resumen" },
  { value: "board", label: "Tablero de control" },
  { value: "client", label: "Por cliente" },
  { value: "technicians", label: "Técnicos" },
  { value: "accounts", label: "Accounts" },
  { value: "costs", label: "Costes" },
  { value: "prices", label: "Precios" },
  { value: "margins", label: "Márgenes" },
] as const;
type TabValue = (typeof TABS)[number]["value"];

/**
 * Tablero de control: lo previsto en los briefings y en el Business Plan frente a lo real, por
 * cliente, mes, formato, técnico y account; y los costes, precios y márgenes de compra de
 * medios. La pestaña, el año, el cliente y el account van en la URL.
 */
const ControlBoard: React.FC = () => {
  const theme = useTheme();
  const collapsed = useSelector(selectSidebarCollapsed);
  const { role } = useUserRole();
  const canSee = !!role && CONTROL_BOARD_ROLES.includes(role);
  const [search, setSearch] = useSearchParams();

  const currentYear = new Date().getFullYear();
  const year = Number(search.get("year")) || currentYear;
  const tabParam = search.get("tab") as TabValue | null;
  const tab: TabValue = TABS.some((t) => t.value === tabParam)
    ? (tabParam as TabValue)
    : "summary";
  const clientId = search.get("client") ?? "";
  const account = search.get("account") ?? "";

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    Object.entries(changes).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k)
    );
    setSearch(next, { replace: true });
  };

  const { data, isLoading, isFetching, error } = useGetControlBoardQuery(year, {
    skip: !canSee,
  });
  const view = data?.data ?? null;
  const board = useMemo(() => (view ? buildBoardData(view) : null), [view]);

  const errorMessage =
    error && "data" in error
      ? ((error.data as { message?: string } | undefined)?.message ?? null)
      : null;

  const tabProps = board
    ? {
        data: board,
        onOpenClient: (id: string) => update({ tab: "client", client: id }),
        onOpenAccount: (name: string) =>
          update({ tab: "accounts", account: name }),
      }
    : null;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Sidebar />
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: { xs: 2, md: 3 },
          transition: "margin-left 0.3s ease",
          ...(collapsed
            ? { ml: `calc(${theme.spacing(7)} + 12px)` }
            : { ml: `calc(${theme.spacing(32)} + 12px)` }),
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <PageBreadcrumbs />

        {!canSee ? (
          <Alert severity="info">
            El tablero de control solo está disponible para Admin y Pricing.
          </Alert>
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[200]}`,
                p: { xs: 2, md: 3 },
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                gap={2}
              >
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Typography variant="h5">
                    Tablero de control {year}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Facturación y beneficio bruto previstos frente a reales,
                    Business Plan, margen por responsable y costes, precios y
                    márgenes de compra de medios.
                    {view
                      ? ` Datos reales hasta el ${view.dataUntil.split("-").reverse().join("/")}${
                          view.planImport
                            ? ` · Business Plan: ${view.planImport.fileName}`
                            : " · sin Business Plan"
                        }.`
                      : ""}
                  </Typography>
                </Box>
                <Stack direction="row" gap={1.5} alignItems="center">
                  {isFetching && <CircularProgress size={18} />}
                  <FormControl size="small" sx={{ minWidth: 110 }}>
                    <InputLabel id="cb-year">Año</InputLabel>
                    <Select
                      labelId="cb-year"
                      label="Año"
                      value={year}
                      onChange={(e) =>
                        update({
                          year: String(e.target.value),
                          client: null,
                          account: null,
                        })
                      }
                    >
                      {[
                        currentYear + 1,
                        currentYear,
                        currentYear - 1,
                        currentYear - 2,
                      ].map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    component={RouterLink}
                    to={`/control-board/business-plan?year=${year}`}
                    variant="outlined"
                    size="small"
                    startIcon={<TableChartIcon />}
                  >
                    Business Plan
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            <Tabs
              value={tab}
              onChange={(_, value: TabValue) => update({ tab: value })}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ borderBottom: `1px solid ${theme.palette.grey[200]}` }}
            >
              {TABS.map((t) => (
                <Tab key={t.value} value={t.value} label={t.label} />
              ))}
            </Tabs>

            {isLoading && (
              <Stack alignItems="center" py={6} gap={1}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Cargando las líneas de {year}…
                </Typography>
              </Stack>
            )}
            {error && (
              <Alert severity="error">
                {errorMessage ?? "No se ha podido cargar el tablero."}
              </Alert>
            )}
            {board && board.lastMonth === 0 && (
              <Alert severity="info">
                {year} todavía no ha empezado: solo hay lo previsto, sin datos
                reales.
              </Alert>
            )}
            {board &&
              !board.hasPlan &&
              tab !== "costs" &&
              tab !== "prices" &&
              tab !== "margins" && (
                <Alert severity="info">
                  No hay Business Plan subido para {year}. Se puede subir desde{" "}
                  <RouterLink to={`/control-board/business-plan?year=${year}`}>
                    la página del Business Plan
                  </RouterLink>
                  .
                </Alert>
              )}

            {tabProps && board && board.lastMonth > 0 && (
              <>
                {tab === "summary" && <SummaryTab {...tabProps} />}
                {tab === "board" && <BoardTab {...tabProps} />}
                {tab === "client" && (
                  <ClientTab
                    {...tabProps}
                    clientId={clientId}
                    onClientChange={(id) => update({ client: id || null })}
                  />
                )}
                {tab === "technicians" && <TechniciansTab {...tabProps} />}
                {tab === "accounts" && (
                  <AccountsTab
                    {...tabProps}
                    selected={account}
                    onSelect={(name) => update({ account: name || null })}
                  />
                )}
                {tab === "costs" && <CostsTab {...tabProps} />}
                {tab === "prices" && <PricesTab {...tabProps} />}
                {tab === "margins" && <MarginsTab {...tabProps} />}
                <Typography variant="caption" color="text.secondary">
                  Facturación prevista = inversión de las líneas de briefing,
                  repartida por días entre los meses de la línea. Facturación
                  real = venta con margen que dan las plataformas (el paid media
                  que paga el cliente, a coste). Beneficio bruto = facturación −
                  coste de plataforma. El año se cuenta de enero a{" "}
                  {MONTHS[board.lastMonth].toLowerCase()}, el último mes con
                  datos; el Business Plan, en esos mismos meses. Semáforos:
                  cumplimiento verde desde el 98 % y ámbar desde el 90 %;
                  diferencias de margen verde si no bajan y ámbar hasta −3 pp.
                </Typography>
              </>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default ControlBoard;
